import React, { createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/components/atoms";
import "./CmsForm.scss";

const CmsFormSavingContext = createContext(false);

/** Whether the enclosing CmsForm is currently saving. */
export const useCmsFormSaving = (): boolean => useContext(CmsFormSavingContext);

interface CmsFormPageProps {
  backTo: string;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  children: React.ReactNode;
  isSaving?: boolean;
  /** Blocks Cancel, Save and back navigation without affecting saving UI or field disabled state. */
  disableActions?: boolean;
  fieldsClassName?: string;
  submitLabel?: string;
  savingLabel?: string;
  submitTrackSuffix?: string;
  cancelTrackSuffix?: string;
  showBackLink?: boolean;
}

/**
 * Reusable layout for backoffice create/edit forms.
 */
export function CmsForm({
  backTo,
  onSubmit,
  children,
  isSaving = false,
  disableActions = false,
  fieldsClassName,
  submitLabel = "Guardar",
  savingLabel = "Guardando...",
  submitTrackSuffix = "bo_form_save",
  cancelTrackSuffix = "bo_form_cancel",
  showBackLink = true,
}: CmsFormPageProps) {
  const navigate = useNavigate();
  const actionsLocked = isSaving || disableActions;

  const handleBack = (): void => {
    if (!actionsLocked) {
      navigate(backTo);
    }
  };

  return (
    <CmsFormSavingContext.Provider value={isSaving}>
      <div className="cms-form-page">
        <div className="cms-form-page__header">
          {showBackLink && (
            <div
              className="cms-form-page__back"
              role="button"
              tabIndex={actionsLocked ? -1 : 0}
              aria-disabled={actionsLocked || undefined}
              onClick={handleBack}
              onKeyDown={(event) => {
                if (actionsLocked) return;
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  navigate(backTo);
                }
              }}
            >
              <ChevronLeft size={18} />
              Volver
            </div>
          )}
        </div>

        <form className="cms-form-page__form" onSubmit={onSubmit} noValidate>
          <div
            className={["cms-form-page__fields", fieldsClassName]
              .filter(Boolean)
              .join(" ")}
          >
            {children}
          </div>

          <div className="cms-form-page__actions">
            <Button
              type="button"
              variant="secondary"
              actionTrackSufix={cancelTrackSuffix}
              onClick={() => {
                if (!actionsLocked) navigate(backTo);
              }}
              disabled={actionsLocked}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              actionTrackSufix={submitTrackSuffix}
              disabled={actionsLocked}
            >
              {isSaving ? savingLabel : submitLabel}
            </Button>
          </div>
        </form>
      </div>
    </CmsFormSavingContext.Provider>
  );
}
